import ConnectionManager from './connection/ConnectionManager';
import IConnection, {MessageEvent} from './connection/IConnection';
import RequestToken from './tokens/RequestToken';
import { v1 } from './requests';
import Session = v1.session.Session;
import Expression = v1.expression.Expression;
import Display = v1.display.Display;
import Listen = v1.listen.Listen;
import Media = v1.media.Media;
import Perception = v1.perception.Perception;
import Assets = v1.assets.Assets;
import Config = v1.config.Config;
import {typeguards} from '@jibo/command-protocol';
import {Event} from './events/Event';
import {generateTransactionID} from './UUID';
import {HotWords} from './requests/v1/listen/requesters/HotWord';


/**
 * Entry point for the Remote Client Protocol
 * @class CommandRequester
 * @example
 * const commandRequester = new CommandRequester();
 * commandRequester.disconnected.on((data) => {
 *     console.log('Connection closed because', data);
 * });
 * await commandRequester.connect(robotName);
 */
export class CommandRequester {
    public assets:Assets;
    public config:Config;
    public display:Display;
    public expression:Expression;
    public listen: Listen;
    public media:Media;
    public perception:Perception;

    /**
     * ID for the app
     * @name CommandRequester#AppID
     * @type string
     * @intdocs
     */
    public AppID:string;

    /**
     * Event emitted when the connection is closed by the robot or a connection issue as
     * <br /> ```Event<{code:number, reason:string}>``` 
     * @name CommandRequester#disconnected
     * @type Event<number, string>
     */
    public disconnected: Event<{code:number, reason:string}>;

    private session:Session;
    private sessionId:string;
    private robotVersion:string;
    private connection:IConnection;
    private robotName:string;
    private inProgressTokens:Map<string, RequestToken<any, any>>;

    constructor() {
        this.robotVersion = '1.0';
        this.AppID = '';
        this.sessionId = null;
        this.inProgressTokens = new Map();
        this.connection = new ConnectionManager();
        this.connection.message.on(this.onMessage.bind(this));
        this.connection.disconnected.on((data) => {
            this.disconnected.emit({code:data.code, reason: data.reason});
        });
        this.assets = new Assets(this);
        this.config = new Config(this);
        this.display = new Display(this);
        this.expression = new Expression(this);
        this.listen = new Listen(this);
        this.media = new Media(this);
        this.perception = new Perception(this);
        this.session = new Session(this);
        this.disconnected = new Event('Disconnected');
    }

    /**
     * Connect to the specified robot. Overridden in the native languages
     * @method CommandRequester#connect
     * @param  {string}        robotName your-friendly-robot-name
     * @return {Promise<void>}
     * @intdocs
     */
    public connect(robotName:string, options?: any):Promise<void> {

        this.robotName = robotName;
        console.log('CommandRequester : connect');
        return (this.connection as ConnectionManager).connect(robotName, options)
        .then(() => {
            console.log('CommandRequester : connected, start session');
            //eat result of connect, start session with robot
            const token = this.session.start();
            return token.complete.then((result) => {
                console.log('CommandRequester : session started, result: ', result);
                //get the session data that we need, then the connection is complete and robot is ready
                this.robotVersion = result.ResponseBody.Version;
                this.sessionId = result.ResponseBody.SessionID;
            });
        });
    }

    /**
     * Disconnect all active robot connections.
     * Overridden in the native languages.
     * @method CommandRequester#disconnect
     * @intdocs
     */
    public disconnect():void {
        this.connection.close();
    }

    /*
     * Send request for the specified asset
     * @method CommandRequester#sendAssetRequest
     * @param {string}        uri      URI for the asset to request
     * @param {AssetCallback} callback `http.IncomingMessage`
     */
    // public sendAssetRequest(uri:string, callback:AssetCallback) {
    //     this.connection.sendAssetRequestTo(this.robotName, uri, callback);
    // }

    private generateTransactionID():string {
        return generateTransactionID();
    }

    /**
     * @private
     */
    public sendRequest(command:JIBO.v1.BaseCommand):string {
        //TODO: Actual current IP address here
        const requestId = this.generateTransactionID();
        const header:JIBO.v1.RequestHeader = {
            TransactionID: requestId,
            SessionID: this.sessionId,
            Version: this.robotVersion,
            Credentials: null,
            AppID: this.AppID
        };
        //Send the request!
        this.connection.sendTo(this.robotName, {
            ClientHeader:header,
            Command: command as any
        });
        //return the id to be applied to the token
        return requestId;
    }

    /**
     * @private
     */
    public sendToken(token:RequestToken<any>):void {
        //Send the request!
        token.requestId = this.sendRequest(token.protocol);
        //Retain tokens as in progress requests, and handle their responses
        if (!token.isComplete) {
            this.inProgressTokens.set(token.requestId, token);
        }
    }

    private onMessage(data: MessageEvent) {
        const response = data.data;
        let id:string = null;
        let ack:JIBO.v1.Acknowledgement = null;
        let event:JIBO.v1.EventMessage = null;
        if (typeguards.isAcknowledgement(response)) {
            id = response.ResponseHeader.TransactionID;
            ack = response;
        } else if(typeguards.isEvent(response)) {
            id = response.EventHeader.TransactionID;
            event = response;
        } else {
            //Invalid?
            return;
        }
        //go through active tokens to see who gets what
        if (this.inProgressTokens.has(id)) {
            const token = this.inProgressTokens.get(id);
            if (ack) {
                token.handleAck(ack);
            } else {
                token.handleEvent(event);
            }
            if (token.isComplete) {
                this.inProgressTokens.delete(id);
            }
        }
    }
}

// Merge interfaces with classes to have interfaces available as CommandRequester.namespace.interface
export namespace CommandRequester {

    /**
     * Definition for making Jibo twist to a certain angle
     * @interface CommandRequester.AngleVector
     * @prop theta {number} Twist/horizontal angle
     * @prop psi {number} Vertical angle
     */
    export interface AngleVector {
        theta: number;
        psi: number;
    }

    /**
     * Definition for a point on Jibo's screen
     * @interface CommandRequester.Vector2
     * @prop x {number} Horizontal pixels
     * @prop y {number} Vertical pixels
     */
    export interface Vector2 {
        x: number;
        y: number;
    }

    /**
     * Definition for a 3D point in space
     * @interface CommandRequester.Vector3
     * @prop x {number} Meters forward
     * @prop y {number} Meters left
     * @prop z {number} Meters up
     */ 
    export interface Vector3 {
        x: number;
        y: number;
        z: number;
    }

    export namespace listen {
        export import HotWord = HotWords;
    }

    export namespace media {

        /**
         * Which camera to use
         * @typedef CommandRequester.media.Camera
         * @prop left {'Left'} Default; Use for photo-taking 
         * @prop right {'Right'} Unsupported
         */
        export enum Camera {
            left = 'Left',
            right = 'Right',
        }

        /**
         * Enum of photo resolution options
         * @typedef CommandRequester.media.CameraResolution
         * @prop high {'highRes'} Currently unsupported
         * @prop med {'medRes'} Higher res than default
         * @prop low {'lowRes'} Default
         * @prop micro {'microRes'} Lower res than default
         */
        export enum CameraResolution {
            high = 'highRes',
            med = 'medRes',
            low = 'lowRes',
            micro = 'microRes',
        }

        /**
         * Enum of video type options
         * @typedef CommandRequester.media.VideoType
         * @prop normal {'NORMAL'} Default
         * @prop debug {'DEBUG'} Currently unsupported
         */
        export enum VideoType {
            normal = 'NORMAL',
            /** Currently unsupported */
            debug = 'DEBUG',
        }
    }

    export namespace config {

        /**
         * Robot configuration options that can be set by your app
         * @interface CommandRequester.config.SetConfigOptions
         * @prop mixer {number} Volume between 0 (mute) and 1 (loudest)
         */
        export interface SetConfigOptions {
            mixer: number;
        }
    }

    export namespace expression {

        /**
         * What type of lookAt Jibo should perform: 
         * <br /> `"ANGLE" | "ENTITY" | "POSITION" | "CAMERA"`
         * @typedef CommandRequester.expression.LookAtTargetType
         */
        export type LookAtTargetType = "ANGLE" | "ENTITY" | "POSITION" | "CAMERA";
    
        /**
         * Base interface for lookAt targets
         * @interface CommandRequester.expression.BaseLookAtTarget
         * @prop type {CommandRequester.expression.LookAtTargetType} Type of lookAt to perform
         * @prop levelHead {boolean} `true` to keep Jibo's head level while he moves
         */
        export interface BaseLookAtTarget {
            type: LookAtTargetType;
            levelHead: boolean;
        }
    
        /**
         * Interface for looking towards an angle.
         * @interface CommandRequester.expression.Angle
         * @extends CommandRequester.expression.BaseLookAtTarget
         * @prop type="ANGLE" {CommandRequester.expression.LookAtTargetType}
         * @prop angle {CommandRequester.AngleVector} Angle to twist to look towards.
         */        
        export interface Angle extends BaseLookAtTarget {
            type: "ANGLE";
            angle: AngleVector;
        }

        /**
         * Interface for looking at a face (entity)
         * @interface CommandRequester.expression.LookAtEntity
         * @extends CommandRequester.expression.BaseLookAtTarget
         * @prop type="ENTITY" {CommandRequester.expression.LookAtTargetType}
         * @prop target {number} ID of the face (entity) to look toward.
         */
        export interface LookAtEntity extends BaseLookAtTarget {
            type: "ENTITY";
            target: number;
        }
    
        /**
         * Interface for looking towards a 3D position in space
         * @interface CommandRequester.expression.Position
         * @extends CommandRequester.expression.BaseLookAtTarget
         * @prop type="POSITION" {CommandRequester.expression.LookAtTargetType}
         * @prop position {CommandRequester.Vector3} 3D point in space to look toward
         */
        export interface Position extends BaseLookAtTarget {
            type: "POSITION";
            position: Vector3;
        }
    
        /**
         * Interface for looking towards a 2D position relative to Jibo's screen
         * @interface CommandRequester.expression.ScreenCoords
         * @extends CommandRequester.expression.BaseLookAtTarget
         * @prop type="CAMERA" {CommandRequester.expression.LookAtTargetType}
         * @prop coords {CommandRequester.Vector2} 2D pixel point on Jibo's screen to look toward
         */
        export interface ScreenCoords extends BaseLookAtTarget {
            type: "CAMERA";
            coords: Vector2;
        }
    
        /**
         * Type of lookAt target
         * @typedef CommandRequester.expression.LookAtTarget
         * @prop Angle {CommandRequester.expression.Angle} Twist/angle target
         * @prop LookAtEntity {CommandRequester.expression.LookAtEntity} Face target
         * @prop Position {CommandRequester.expression.Position} 3D position target
         * @prop ScreenCoords {CommandRequester.expression.ScreenCoords} 2D pixel target
         */
        export type LookAtTarget = Angle | LookAtEntity | Position | ScreenCoords;
    
        /**
         * Enum of Jibo's available attention modes.
         * @typedef CommandRequester.expression.AttentionMode
         * @prop Off
         * @prop Idle
         * @prop Disengage
         * @prop Engaged
         * @prop Speaking
         * @prop Fixated
         * @prop Attractable
         * @prop Menu
         * @prop Command
         */
        export enum AttentionMode {
            Off = 'OFF',
            Idle = 'IDLE',
            Disengage = 'DISENGAGE',
            Engaged = 'ENGAGED',
            Speaking = 'SPEAKING',
            Fixated = 'FIXATED',
            Attractable = 'ATTRACTABLE',
            Menu = 'MENU',
            Command = 'COMMAND',
        }
    }

    export namespace display {

        /**
         * Options for listening for screen gestures
         * @interface CommandRequester.display.ScreenGestureFilter
         * @prop [area] {CommandRequester.display.Circle | CommandRequester.display.Rectangle} Area in which to listen for a screen gesture
         * @prop [type] {CommandRequester.display.ScreenGestureType} Type of gesture to listen for
         */
        export interface ScreenGestureFilter {
            area?: Circle | Rectangle;
            type?: ScreenGestureType;
        }
        
        /**
         * Definition for a circular area on Jibo's screen
         * @interface CommandRequester.display.Circle
         * @prop radius {number} Radius of the circle in pixels
         * @prop x {number} Horizontal coordinate of the circle's center in pixels
         * @prop y {number} Vertical coordinate of the circle's center in pixels
         */
        export interface Circle {
            radius: number;
            x: number;
            y: number;
        }

        /**
         * Definition for a rectangular area on Jibo's screen
         * @interface CommandRequester.display.Rectangle
         * @prop height {number} Height of the rectangle in pixels
         * @prop width {number} Width of the rectangle in pixels
         * @prop x {number} Horizontal coordinate of the top-left corner of the rectangle in pixels
         * @prop y {number} Vertical coordinate of the top-left corner of the rectangle in pixels
         */
        export interface Rectangle {
            height: number;
            width: number;
            x: number;
            y: number;
        }

        /**
         * Enum of screen gesture types
         * @typedef CommandRequester.display.ScreenGestureType
         * @prop Tap 'TAP' A tap on Jibo's screen
         * @prop SwipeUp 'SWIPEUP' A swipe from bottom to top on Jibo's screen
         * @prop SwipeDown 'SWIPEDOWN' A swipe from top to bottom on Jibo's screen
         * @prop SwipeLeft 'SWIPELEFT' A swipe from right to left on Jibo's screen
         * @prop SwipeRight 'SWIPERIGHT' A swipe from left to right on Jibo's screen
         */
        export enum ScreenGestureType {
            Tap = 'TAP',
            SwipeUp = 'SWIPEUP',
            SwipeDown = 'SWIPEDOWN',
            SwipeLeft = 'SWIPELEFT',
            SwipeRight = 'SWIPERIGHT',
        }

        /**
         * Data object for image info
         * @interface CommandRequester.display.ImageData
         * @prop name {string} Provide a unique name for the asset in the local cache
         * @prop [set] {string} Previously defined name of set of asssets to add this asset to
         * @prop src {string} URL to the image
         */
        export interface ImageData {
            name: string;
            set?: string;
            src: string;
        }
    }
}